import { useState } from "react";
import { Col, CardTitle, Card, Button, Row } from "reactstrap";
import {
  RemoveAplPeriod,
  RenameAplPeriod,
  UpdateAplPeriodDates,
} from "../../actions/teacherMutations";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import ConfirmDialog from "../Modal/ConfirmDialog";
import ChangeAPLPeriodDateModal from "../Modal/ChangeAPLPeriodDateModal";
import RenameAPLPeriodModal from "../Modal/RenameAPLPeriodModal";
import { isValid, parseISO } from "date-fns";
import { dateIsAfter, formatDate } from "../../Utilities/dateUtils";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

export default function RemoveAndChangeDateApl({
  periodId,
  periodName,
  currentStartDate,
  currentEndDate,
  level,
  objId,
  setPeriod,
}) {
  const mutateRemoveAplPeriod = useMutation({ mutationFn: RemoveAplPeriod });
  const mutateUpdateAplPeriod = useMutation({
    mutationFn: UpdateAplPeriodDates,
  });
  const mutateRenameAplPeriod = useMutation({ mutationFn: RenameAplPeriod });
  const intl = useIntl();

  const [promptForDelete, setPromptForDelete] = useState(false);
  const [promptForEnd, setPromptForEnd] = useState(false);
  const [promptForChangeDates, setPromptForChangeDates] = useState(false);
  const [promptForRename, setPromptForRename] = useState(false);
  const [newName, setNewName] = useState(periodName);
  const [updateWorkRegistrationTitles, setUpdateWorkRegistrationTitles] =
    useState(false);
  const queryClient = useQueryClient();

  const parsedDate = (date) =>
    date && date !== "" && isValid(parseISO(date))
      ? parseISO(date)
      : new Date();

  const [newStartDate, setNewStartDate] = useState(
    parsedDate(currentStartDate)
  );
  const [newEndDate, setNewEndDate] = useState(parsedDate(currentEndDate));

  const handleEndPeriod = () => {
    if (dateIsAfter(currentEndDate)) {
      mutateUpdateAplPeriod.mutate(
        {
          periodId: periodId,
          startDate: currentStartDate,
          endDate: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        },
        {
          onSuccess: (data) => {
            if (data?.status > 199 && data.status < 300) {
              setPromptForEnd(false);
              toast.success(<FormattedMessage id="PeriodEnded" />);
              queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
            } else {
              toast.warn(data);
            }
          },
          onError: (data) => {
            setPromptForEnd(false);
            toast.warn(data.response.data);
          },
        }
      );
    } else {
      setPromptForEnd(false);
      toast.warn(<FormattedMessage id="EndDateForPeriodAlreadyPassed" />);
    }
  };

  const handleDeletePeriod = () => {
    mutateRemoveAplPeriod.mutate(
      { periodId: periodId },
      {
        onSuccess: (data) => {
          if (data?.status > 199 && data.status < 300) {
            setPromptForDelete(false);
            toast.success(<FormattedMessage id="PeriodDeleted" />);
            setPeriod(null);
            queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
          } else {
            toast.warn(data ? data : "");
          }
        },
        onError: (data) => {
          setPromptForEnd(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangeStartOrEndDates = (startDate, endDate) => {
    mutateUpdateAplPeriod.mutate(
      {
        periodId: periodId,
        startDate: formatDate(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        endDate: formatDate(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      },
      {
        onSuccess: (data) => {
          if (data?.status > 199 && data.status < 300) {
            setPromptForChangeDates(false);
            toast.success(<FormattedMessage id="DatesChanged" />);
            queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
          } else {
            toast.warn(data ? data : "");
            setNewStartDate(parsedDate(currentStartDate));
            setNewEndDate(parsedDate(currentEndDate));
          }
        },
        onError: (data) => {
          setNewStartDate(parsedDate(currentStartDate));
          setNewEndDate(parsedDate(currentEndDate));
          toast.warn(data.response.data);
        },
      }
    );
  };

  const openRename = () => {
    setNewName(periodName);
    setUpdateWorkRegistrationTitles(false);
    setPromptForRename(true);
  };

  const handleRenamePeriod = () => {
    mutateRenameAplPeriod.mutate(
      {
        periodId: periodId,
        newName: newName.trim(),
        updateWorkRegistrationTitles: updateWorkRegistrationTitles,
      },
      {
        onSuccess: (data) => {
          if (data?.status > 199 && data.status < 300) {
            setPromptForRename(false);
            toast.success(<FormattedMessage id="PeriodRenamed" />);
            queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
            if (updateWorkRegistrationTitles) {
              queryClient.invalidateQueries({ queryKey: ["FetchEvents"] });
            }
          } else {
            toast.warn(
              typeof data === "string" ? formatServiceError(intl, data) : data,
            );
          }
        },
        onError: (data) => {
          toast.warn(
            formatServiceError(intl, data?.response?.data) || data.message,
          );
        },
      }
    );
  };

  return (
    <Col lg={12}>
      <Card body>
        <CardTitle className="mb-0">
          <FormattedMessage id="Edit" /> - {periodName}
          <hr />
        </CardTitle>
        <Row>
          <Col lg={6}>
            <Button
              color="primary"
              className="mx-1"
              outline
              onClick={() => setPromptForDelete(true)}
            >
              <FormattedMessage id="RemovePeriod" />
            </Button>
            <Button
              color="primary mx-1"
              className="mx-1"
              outline
              onClick={() => setPromptForEnd(true)}
            >
              <FormattedMessage id="EndPeriod" />
            </Button>
            <Button
              color="primary mx-1"
              className="mx-1"
              outline
              onClick={() => setPromptForChangeDates(true)}
            >
              <FormattedMessage id="ChangeDates" />
            </Button>
            <Button
              color="primary"
              className="mx-1"
              outline
              onClick={openRename}
            >
              <FormattedMessage id="Rename" />
            </Button>
          </Col>
        </Row>
      </Card>
      <ConfirmDialog
        danger={true}
        isOpen={promptForDelete}
        toggle={() => setPromptForDelete(!promptForDelete)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ periodName: periodName }}
            id="AplPeriodWillBeDeleted"
          />
        }
        onProceed={handleDeletePeriod}
      />
      <ConfirmDialog
        isOpen={promptForEnd}
        toggle={() => setPromptForEnd(!promptForEnd)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ periodName: periodName }}
            id="AplPeriodWillEnd"
          />
        }
        onProceed={handleEndPeriod}
        danger={true}
      />
      <ChangeAPLPeriodDateModal
        periodName={periodName}
        currentStartDate={currentStartDate}
        currentEndDate={currentEndDate}
        isOpen={promptForChangeDates}
        toggle={() => setPromptForChangeDates(!promptForChangeDates)}
        newStartDate={newStartDate}
        newEndDate={newEndDate}
        setNewStartDate={setNewStartDate}
        setNewEndDate={setNewEndDate}
        handleChangeStartOrEndDates={handleChangeStartOrEndDates}
      />
      <RenameAPLPeriodModal
        periodName={periodName}
        isOpen={promptForRename}
        toggle={() => setPromptForRename(!promptForRename)}
        newName={newName}
        setNewName={setNewName}
        updateWorkRegistrationTitles={updateWorkRegistrationTitles}
        setUpdateWorkRegistrationTitles={setUpdateWorkRegistrationTitles}
        handleRename={handleRenamePeriod}
      />
    </Col>
  );
}
